/**
 * @file routes/_app/tc/index.tsx
 * @description TC（交易憑證）文件列表頁（M3）。
 *
 * 功能：
 * - 使用 ListPageShell 展示所有 TC 文件（分頁）
 * - 搜尋：TC 編號 / 申請者 / 買方 / 產品
 * - 狀態篩選（待審核/已核發/已撤銷）
 * - 詳情 Drawer：DataFieldGrid + DataField + 審核歷史 Timeline
 * - Admin/Manager：「新增 TC」頁內 Modal（原獨立 /tc/new 頁已併入）
 *
 * 角色：所有登入角色可查看；admin/manager 可新建 TC
 */

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import {
  AutoComplete,
  Button,
  Col,
  DatePicker,
  Drawer,
  Form,
  Input,
  InputNumber,
  Modal,
  Radio,
  Row,
  Select,
  Space,
  Table,
  Timeline,
  Tooltip,
} from 'antd';
import { useMutation, useQuery } from '@tanstack/react-query';
import type { Dayjs } from 'dayjs';
import { message } from '@/lib/antd-app';
import type { ColumnsType } from 'antd/es/table';
import {
  CalendarDays,
  CheckCircle,
  Clock,
  Eye,
  FileText,
  Hash,
  Plus,
  Search,
  Truck,
  XCircle,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { apiClient } from '@/lib/api-client';
import { useAuthStore } from '@/lib/auth-context';
import {
  DataField,
  DataFieldGrid,
  EmptyState,
  ListPageShell,
  PageMetaItem,
  StatusPill,
} from '@/components/shell';
import { color, font, leading, radius, space, text, tracking, weight } from '@/styles/tokens';

/** TanStack Router 路由定義 */
export const Route = createFileRoute('/_app/tc/')({ component: TcListPage });

// ─── 型別定義 ─────────────────────────────────────────────────────────────────

/** 後端 GET /api/tc 回傳的列表項 */
interface ApiTcItem {
  id: number;
  code: string;
  certificateId: number;
  clientId: number;
  transactionType: string;
  productItem: string;
  quantity: string;
  unit: string;
  transactionDate: string;
  buyerName: string;
  issuedAt: string;
  voidedAt: string | null;
  certNo: string;
  clientName: string;
}

interface ApiTcList {
  items: ApiTcItem[];
  total: number;
}

/** UI 展示型別 */
interface TradeCertificate {
  id: number;
  tcNumber: string;
  applicantName: string;
  buyerName: string;
  productName: string;
  quantity: number;
  unit: string;
  organicRatio: string;
  status: '待審核' | '已核發' | '已撤銷';
  certNumber: string;
  applyDate: string;
  buyerAddress: string;
  remarks: string;
  auditHistory: { time: string; action: string; operator: string }[];
}

// ─── 轉換函數 ─────────────────────────────────────────────────────────────────

/** 將後端 API 項轉換為 UI 展示型別 */
function mapApiToTc(item: ApiTcItem): TradeCertificate {
  const status: TradeCertificate['status'] = item.voidedAt ? '已撤銷' : '已核發';
  return {
    id: item.id,
    tcNumber: item.code,
    applicantName: item.clientName,
    buyerName: item.buyerName ?? '—',
    productName: item.productItem,
    quantity: parseFloat(item.quantity),
    unit: item.unit,
    organicRatio: '100%',
    status,
    certNumber: item.certNo,
    applyDate: item.issuedAt?.slice(0, 10) ?? '—',
    buyerAddress: '',
    remarks: '',
    auditHistory: [
      { time: item.issuedAt?.slice(0, 10) ?? '—', action: '核發 TC', operator: '系統' },
      ...(item.voidedAt
        ? [{ time: item.voidedAt.slice(0, 10), action: '作廢 TC', operator: '系統' }]
        : []),
    ],
  };
}

// ─── 常量 ─────────────────────────────────────────────────────────────────────

/** TC 狀態 → StatusPill 配置 */
const STATUS_TONE_MAP: Record<
  TradeCertificate['status'],
  { tone: 'warning' | 'success' | 'danger'; dot: boolean }
> = {
  待審核: { tone: 'warning', dot: true },
  已核發: { tone: 'success', dot: false },
  已撤銷: { tone: 'danger', dot: false },
};

// ─── 新增 TC Modal ────────────────────────────────────────────────────────────

/** 證書搜尋選項（Autocomplete 資料源） */
interface CertOption {
  id: number;
  certNo: string;
  clientName: string;
  status: string;
}

/** 新增 TC 表單值 */
interface NewTcFormValues {
  certificateId: number;
  transactionType: string;
  productItem: string;
  quantity: number;
  unit: string;
  transactionDate: Dayjs;
  buyerName?: string;
  buyerTaxId?: string;
  notes?: string;
}

/** POST /api/tc 回傳的新建項 */
interface CreatedTc {
  id: number;
  code: string;
}

/**
 * 新增 TC 彈窗（原獨立 /tc/new 頁併入）。
 * 建立成功後跳轉至 /tc/:id 詳情頁。
 */
function NewTcModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const navigate = useNavigate();
  const [form] = Form.useForm<NewTcFormValues>();
  const [certSearch, setCertSearch] = useState('');
  const [selectedCertId, setSelectedCertId] = useState<number | null>(null);

  // 搜尋證書 Autocomplete
  const { data: certData } = useQuery<{ items: CertOption[] }>({
    queryKey: ['certs-for-tc', certSearch],
    queryFn: () =>
      apiClient.get(
        `/api/certificates?pageSize=20${certSearch ? `&q=${encodeURIComponent(certSearch)}` : ''}`,
      ),
    enabled: open && certSearch.length >= 1,
  });

  const certOptions = (certData?.items ?? []).map((c) => ({
    value: `${c.certNo} — ${c.clientName}`,
    label: `${c.certNo} — ${c.clientName}（${c.status}）`,
    certId: c.id,
  }));

  // 建立 TC mutation
  const createMutation = useMutation({
    mutationFn: (body: {
      certificateId: number;
      transactionType: string;
      productItem: string;
      quantity: number;
      unit: string;
      transactionDate: string;
      buyerName?: string;
      buyerTaxId?: string;
      notes?: string;
    }) => apiClient.post('/api/tc', body),
    onSuccess: (res) => {
      const created = (res as { data?: { item?: CreatedTc } }).data?.item;
      if (created) {
        message.success(`TC 文件 ${created.code} 已建立`);
        onClose();
        void navigate({ to: '/tc/$id', params: { id: String(created.id) } });
      }
    },
    onError: (e) => {
      message.error(e instanceof Error ? e.message : '建立 TC 失敗，請稍後再試');
      console.error('create tc failed', e);
    },
  });

  return (
    <Modal
      title="新建交易憑證（TC）"
      open={open}
      onCancel={onClose}
      okText="建立 TC 文件"
      cancelText="取消"
      confirmLoading={createMutation.isPending}
      onOk={() => form.submit()}
      width={560}
      destroyOnHidden
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={(values: NewTcFormValues) => {
          if (!selectedCertId) {
            message.warning('請選擇有效的證書');
            return;
          }
          createMutation.mutate({
            certificateId: selectedCertId,
            transactionType: values.transactionType,
            productItem: values.productItem,
            quantity: values.quantity,
            unit: values.unit,
            transactionDate: values.transactionDate.toISOString(),
            buyerName: values.buyerName,
            buyerTaxId: values.buyerTaxId,
            notes: values.notes,
          });
        }}
      >
        {/* 關聯證書（Autocomplete 搜尋） */}
        <Form.Item
          name="certificateSearch"
          label="關聯證書"
          rules={[
            {
              validator: () =>
                selectedCertId
                  ? Promise.resolve()
                  : Promise.reject(new Error('請從下拉選單選擇證書')),
            },
          ]}
        >
          <AutoComplete
            options={certOptions}
            onSearch={(v) => {
              setCertSearch(v);
              setSelectedCertId(null);
            }}
            onSelect={(_: string, option: { certId: number }) => {
              setSelectedCertId(option.certId);
            }}
            placeholder="輸入證書號碼或客戶名稱搜尋..."
            style={{ width: '100%' }}
            allowClear
            onClear={() => setSelectedCertId(null)}
          />
        </Form.Item>

        {/* 交易類型 */}
        <Form.Item
          name="transactionType"
          label="交易類型"
          rules={[{ required: true, message: '請選擇交易類型' }]}
        >
          <Radio.Group
            options={[
              { value: '生產', label: '生產' },
              { value: '加工', label: '加工' },
              { value: '分裝', label: '分裝' },
              { value: '流通', label: '流通' },
            ]}
          />
        </Form.Item>

        {/* 產品項目 */}
        <Form.Item
          name="productItem"
          label="產品項目"
          rules={[{ required: true, message: '請輸入產品項目' }]}
        >
          <Input placeholder="例：有機稻米（台灣有機）" maxLength={200} />
        </Form.Item>

        {/* 數量 + 單位 */}
        <Form.Item label="數量" required>
          <Space.Compact style={{ width: '100%' }}>
            <Form.Item name="quantity" noStyle rules={[{ required: true, message: '請輸入數量' }]}>
              <InputNumber min={0} step={0.01} style={{ width: '60%' }} placeholder="數量" />
            </Form.Item>
            <Form.Item name="unit" noStyle rules={[{ required: true, message: '請輸入單位' }]}>
              <Input style={{ width: '40%' }} placeholder="例：公斤、公噸、箱" maxLength={20} />
            </Form.Item>
          </Space.Compact>
        </Form.Item>

        {/* 交易日期 */}
        <Form.Item
          name="transactionDate"
          label="交易日期"
          rules={[{ required: true, message: '請選擇交易日期' }]}
        >
          <DatePicker format="YYYY/MM/DD" style={{ width: '100%' }} />
        </Form.Item>

        {/* 買受方資訊（選填） */}
        <Form.Item name="buyerName" label="買受方名稱（選填）">
          <Input placeholder="買受方公司名稱或個人姓名" maxLength={200} />
        </Form.Item>
        <Form.Item name="buyerTaxId" label="買受方統一編號（選填）">
          <Input placeholder="統一編號（8碼）" maxLength={20} />
        </Form.Item>
        <Form.Item name="notes" label="備注（選填）">
          <Input.TextArea rows={3} maxLength={500} placeholder="其他備注..." />
        </Form.Item>
      </Form>
    </Modal>
  );
}

// ─── 主頁面組件 ───────────────────────────────────────────────────────────────

/**
 * TC 文件列表頁面主組件。
 */
function TcListPage() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user!);
  const isManager = user.roles.includes('admin') || user.roles.includes('manager');

  /** 列表資料 */
  const [tcs, setTCs] = useState<TradeCertificate[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const pageSize = 10;
  /** 篩選條件 */
  const [searchText, setSearchText] = useState('');
  const [filterStatus, setFilterStatus] = useState<string | undefined>(undefined);
  /** 詳情 Drawer */
  const [detailDrawerOpen, setDetailDrawerOpen] = useState(false);
  const [viewingTC, setViewingTC] = useState<TradeCertificate | null>(null);
  /** 新增 TC Modal */
  const [newTcOpen, setNewTcOpen] = useState(false);

  // ─── 資料拉取 ────────────────────────────────────────────────────

  const fetchTCs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(page),
        pageSize: String(pageSize),
        includeVoided: 'true',
      });
      if (searchText.trim()) params.set('q', searchText.trim());
      const data = await apiClient.get<ApiTcList>(`/api/tc?${params.toString()}`);
      setTCs(data.items.map(mapApiToTc));
      setTotal(data.total);
    } catch (err) {
      console.error('[tc-list] 拉取 TC 列表失敗:', err);
      message.error('無法載入 TC 列表，請稍後再試');
    } finally {
      setLoading(false);
    }
  }, [page, searchText]);

  useEffect(() => {
    void fetchTCs();
  }, [fetchTCs]);

  // ─── 前端狀態篩選 ─────────────────────────────────────────────────

  const filteredTCs = useMemo(() => {
    if (!filterStatus) return tcs;
    return tcs.filter((t) => t.status === filterStatus);
  }, [tcs, filterStatus]);

  // ─── 統計 ─────────────────────────────────────────────────────────

  const stats = useMemo(
    () => ({
      total: tcs.length,
      pending: tcs.filter((t) => t.status === '待審核').length,
      issued: tcs.filter((t) => t.status === '已核發').length,
      revoked: tcs.filter((t) => t.status === '已撤銷').length,
    }),
    [tcs],
  );

  // ─── 事件處理 ────────────────────────────────────────────────────

  const handleView = (record: TradeCertificate) => {
    setViewingTC(record);
    setDetailDrawerOpen(true);
  };

  // ─── 表格欄位 ─────────────────────────────────────────────────────

  const columns: ColumnsType<TradeCertificate> = [
    {
      title: 'TC 編號',
      dataIndex: 'tcNumber',
      key: 'tcNumber',
      width: 130,
      sorter: (a, b) => a.tcNumber.localeCompare(b.tcNumber),
      render: (v: string) => (
        <span
          style={{
            fontFamily: font.mono,
            fontSize: text.sm,
            color: color.text.primary,
            fontWeight: weight.medium,
          }}
        >
          {v}
        </span>
      ),
    },
    {
      title: '申請者',
      dataIndex: 'applicantName',
      key: 'applicantName',
      width: 150,
      ellipsis: true,
      render: (v: string) => (
        <span style={{ fontWeight: weight.medium, color: color.text.primary }}>{v}</span>
      ),
    },
    {
      title: '買方',
      dataIndex: 'buyerName',
      key: 'buyerName',
      width: 160,
      ellipsis: true,
    },
    {
      title: '產品名稱',
      dataIndex: 'productName',
      key: 'productName',
      width: 130,
    },
    {
      title: '數量',
      key: 'quantityDisplay',
      width: 110,
      render: (_, record) => (
        <span
          style={{
            fontFamily: font.mono,
            fontSize: text.sm,
            color: color.text.secondary,
          }}
        >
          {record.quantity.toLocaleString()} {record.unit}
        </span>
      ),
    },
    {
      title: '有機比例',
      dataIndex: 'organicRatio',
      key: 'organicRatio',
      width: 90,
      render: (r: string) => (
        <StatusPill tone="success" size="sm" variant="outline" mono>
          {r}
        </StatusPill>
      ),
    },
    {
      title: '關聯證書',
      dataIndex: 'certNumber',
      key: 'certNumber',
      width: 200,
      ellipsis: true,
      render: (v: string) => (
        <span style={{ fontFamily: font.mono, fontSize: text.sm, color: color.text.secondary }}>
          {v}
        </span>
      ),
    },
    {
      title: '狀態',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (s: TradeCertificate['status']) => {
        const cfg = STATUS_TONE_MAP[s];
        return (
          <StatusPill tone={cfg.tone} size="sm" dot={cfg.dot}>
            {s}
          </StatusPill>
        );
      },
    },
    {
      title: '操作',
      key: 'action',
      width: 70,
      fixed: 'right',
      render: (_, record) => (
        <Tooltip title="檢視詳情">
          <Button
            type="text"
            size="small"
            icon={<Eye size={14} />}
            onClick={(e) => {
              e.stopPropagation();
              handleView(record);
            }}
          />
        </Tooltip>
      ),
    },
  ];

  return (
    <ListPageShell
      title={
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
          <Truck size={22} color={color.brand.primary} />
          銷售證/貿易證管理
        </span>
      }
      description="管理 TC（Trade Certificate）申請與核發，跨境銷售有機產品時提供官方批次證明文件。"
      meta={
        <>
          <PageMetaItem
            icon={<FileText size={13} />}
            label="總 TC 數"
            value={`${stats.total} 件`}
          />
          <PageMetaItem icon={<Clock size={13} />} label="待審核" value={`${stats.pending} 件`} />
          <PageMetaItem
            icon={<CheckCircle size={13} />}
            label="已核發"
            value={`${stats.issued} 件`}
          />
          <PageMetaItem icon={<XCircle size={13} />} label="已撤銷" value={`${stats.revoked} 件`} />
        </>
      }
      actions={
        isManager ? (
          <Button type="primary" icon={<Plus size={14} />} onClick={() => setNewTcOpen(true)}>
            新增 TC
          </Button>
        ) : undefined
      }
      filters={
        <Row gutter={[12, 8]} style={{ width: '100%' }} align="middle">
          <Col xs={24} sm={12} md={8}>
            <Input
              placeholder="搜尋 TC 編號 / 申請者 / 買方 / 產品"
              prefix={<Search size={14} color={color.text.muted} />}
              value={searchText}
              onChange={(e) => {
                setSearchText(e.target.value);
                setPage(1);
              }}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Select
              placeholder="狀態"
              options={[
                { label: '待審核', value: '待審核' },
                { label: '已核發', value: '已核發' },
                { label: '已撤銷', value: '已撤銷' },
              ]}
              value={filterStatus}
              onChange={setFilterStatus}
              allowClear
              style={{ width: '100%' }}
            />
          </Col>
        </Row>
      }
      toolbar={
        <span style={{ fontSize: text.sm, color: color.text.tertiary }}>
          找到 <strong style={{ color: color.text.primary }}>{filteredTCs.length}</strong> 件 / 共{' '}
          <strong style={{ color: color.text.primary }}>{tcs.length}</strong> 件
        </span>
      }
    >
      {!loading && filteredTCs.length === 0 ? (
        <EmptyState
          variant="empty"
          title="尚無符合條件的 TC"
          description="調整搜尋條件，或建立第一筆 TC 申請。"
          action={
            isManager ? (
              <Button type="primary" icon={<Plus size={14} />} onClick={() => setNewTcOpen(true)}>
                新增 TC
              </Button>
            ) : undefined
          }
          size="md"
        />
      ) : (
        <Table<TradeCertificate>
          columns={columns}
          dataSource={filteredTCs}
          rowKey="id"
          loading={loading}
          scroll={{ x: 1200 }}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: false,
            showTotal: (t) => `共 ${t} 件`,
            onChange: (p) => setPage(p),
          }}
          size="middle"
          onRow={(record) => ({
            onClick: () => handleView(record),
            style: { cursor: 'pointer' },
          })}
        />
      )}

      {/* ════════════ TC 詳情 Drawer ════════════ */}
      <Drawer
        title={
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <Truck size={16} color={color.brand.primary} />
            <span>TC 詳細資訊</span>
          </span>
        }
        open={detailDrawerOpen}
        onClose={() => setDetailDrawerOpen(false)}
        size="large"
        extra={
          isManager && viewingTC ? (
            <Button
              type="primary"
              onClick={() => {
                setDetailDrawerOpen(false);
                void navigate({ to: '/tc/$id', params: { id: String(viewingTC.id) } });
              }}
            >
              前往詳情頁
            </Button>
          ) : null
        }
      >
        {viewingTC && (
          <>
            {/* 編號 + 狀態 */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: `${space[3]}px ${space[4]}px`,
                background: color.surface.muted,
                borderRadius: radius.md,
                border: `1px solid ${color.border.subtle}`,
                marginBottom: space[5],
              }}
            >
              <span
                style={{
                  fontFamily: font.mono,
                  fontSize: text.md,
                  fontWeight: weight.semibold,
                  color: color.text.primary,
                }}
              >
                {viewingTC.tcNumber}
              </span>
              <StatusPill
                tone={STATUS_TONE_MAP[viewingTC.status].tone}
                dot={STATUS_TONE_MAP[viewingTC.status].dot}
              >
                {viewingTC.status}
              </StatusPill>
            </div>

            <DataFieldGrid columns={2}>
              <DataField label="申請者" value={viewingTC.applicantName} />
              <DataField label="買方" value={viewingTC.buyerName} />
              <DataField label="買方地址" value={viewingTC.buyerAddress || '—'} span={2} />
              <DataField label="產品名稱" value={viewingTC.productName} />
              <DataField
                label="數量"
                value={`${viewingTC.quantity.toLocaleString()} ${viewingTC.unit}`}
                mono
              />
              <DataField
                label="有機比例"
                value={
                  <StatusPill tone="success" size="sm" variant="outline" mono>
                    {viewingTC.organicRatio}
                  </StatusPill>
                }
              />
              <DataField
                label="關聯證書"
                icon={<Hash size={11} />}
                value={viewingTC.certNumber}
                mono
              />
              <DataField
                label="申請日期"
                icon={<CalendarDays size={11} />}
                value={viewingTC.applyDate}
                mono
              />
              <DataField label="備註" value={viewingTC.remarks || '—'} span={2} />
            </DataFieldGrid>

            {/* 審核歷史 */}
            <div
              style={{
                marginTop: space[6],
                paddingTop: space[5],
                borderTop: `1px solid ${color.border.subtle}`,
              }}
            >
              <div
                style={{
                  fontSize: text.xs,
                  fontWeight: weight.semibold,
                  color: color.text.muted,
                  letterSpacing: tracking.widest,
                  textTransform: 'uppercase',
                  marginBottom: space[3],
                }}
              >
                審核歷史
              </div>
              <Timeline
                items={viewingTC.auditHistory.map((h) => ({
                  children: (
                    <div>
                      <div
                        style={{
                          fontSize: text.md,
                          fontWeight: weight.semibold,
                          color: color.text.primary,
                          lineHeight: leading.snug,
                        }}
                      >
                        {h.action}
                      </div>
                      <div
                        style={{
                          fontSize: text.sm,
                          color: color.text.tertiary,
                          marginTop: 2,
                        }}
                      >
                        <span style={{ fontFamily: font.mono, color: color.text.secondary }}>
                          {h.time}
                        </span>
                        {' · '}
                        <span>操作人：{h.operator}</span>
                      </div>
                    </div>
                  ),
                }))}
              />
            </div>
          </>
        )}
      </Drawer>

      {/* 新增 TC Modal（原獨立 /tc/new 頁併入） */}
      <NewTcModal open={newTcOpen} onClose={() => setNewTcOpen(false)} />
    </ListPageShell>
  );
}
